import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Avatar,
  Badge,
  Box,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useAppDispatch, useAppSelector } from 'store';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  ContatoSelectInterface,
  contatosSelector,
  fetchContatosDialogV2,
  resetContatosDialogV2,
} from 'store/reducers/contatos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import { BlockedContactName } from './BlockedContactName';
import { ContactAvatar } from './ContactAvatar';

const useStyles = makeStyles(() => ({
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  avatar: {
    width: 24,
    height: 24,
  },
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
}));

const LISTBOX_PADDING = 8;
const DEBOUNCE_DELAY = 1000;

interface ContatoSelectFieldProps {
  contatoId?: string;
  onFilter: (contato: ContatoSelectInterface | null) => void;
  onReset: () => void;
}

export default function ContatoSelectField({
  contatoId,
  onFilter,
  onReset,
}: ContatoSelectFieldProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSearchingRef = useRef(false);

  const [contatoSelecionado, setContatoSelecionado] =
    useState<ContatoSelectInterface | null>(null);

  const { contatosDialogV2, loadingContatosDialogV2, contatosDialogV2Cursor } =
    useAppSelector(contatosSelector);

  const getContatos = useCallback(
    (params?: { search?: string; cursor?: CursorInterface }) => {
      dispatch(fetchContatosDialogV2(params));
    },
    [dispatch],
  );

  const resetAndFetch = useCallback(
    (search?: string) => {
      isSearchingRef.current = true;
      dispatch(resetContatosDialogV2());
      getContatos({ search });
    },
    [dispatch, getContatos],
  );

  const onLoadNextContatos = useCallback(() => {
    const { afterCursor } = contatosDialogV2Cursor;
    if (afterCursor && !loadingContatosDialogV2) {
      getContatos({
        search: searchQuery || undefined,
        cursor: { afterCursor },
      });
    }
  }, [
    contatosDialogV2Cursor,
    searchQuery,
    getContatos,
    loadingContatosDialogV2,
  ]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(inputValue);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue]);

  useEffect(() => {
    if (open && searchQuery !== undefined && !isSearchingRef.current) {
      resetAndFetch(searchQuery || undefined);
    }
  }, [searchQuery, open, resetAndFetch]);

  useEffect(() => {
    if (!loadingContatosDialogV2 && isSearchingRef.current) {
      isSearchingRef.current = false;
    }
  }, [loadingContatosDialogV2]);

  const handleInputChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: string,
  ) => {
    setInputValue(value);
  };

  const handleChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: ContatoSelectInterface | null,
  ) => {
    setContatoSelecionado(value);

    if (value) {
      onFilter(value);
    } else {
      onFilter(null);
      onReset();
    }
  };

  useEffect(() => {
    if (!contatoId) {
      setContatoSelecionado(null);
      return;
    }

    if (contatosDialogV2.length === 0) {
      return;
    }

    const contato = contatosDialogV2.find(c => c.id === contatoId);
    setContatoSelecionado(contato || null);
  }, [contatoId, contatosDialogV2]);

  useEffect(() => {
    if (contatoSelecionado) {
      setInputValue(contatoSelecionado.name);
    }
  }, [contatoSelecionado]);

  return (
    <Autocomplete
      id="contato-select-field"
      value={contatoSelecionado}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={contatosDialogV2}
      loading={loadingContatosDialogV2}
      filterOptions={x => x}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearchQuery('');

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        isSearchingRef.current = false;
      }}
      autoHighlight
      size="small"
      onChange={handleChange}
      popupIcon={<ExpandMore style={{ fontSize: 18 }} />}
      ListboxProps={{
        onLoadMore: onLoadNextContatos,
        hasMore: Boolean(contatosDialogV2Cursor.afterCursor),
        loading: loadingContatosDialogV2,
      }}
      ListboxComponent={
        ListboxComponent as unknown as React.ComponentType<
          React.HTMLAttributes<HTMLElement>
        >
      }
      classes={{
        listbox: classes.listbox,
      }}
      disableListWrap
      closeText="Fechar"
      openText="Abrir"
      clearText="Limpar"
      getOptionLabel={option => option?.name || ''}
      getOptionSelected={(option, value) => option.id === value.id}
      noOptionsText={
        <Typography variant="body2">Nenhum contato encontrado</Typography>
      }
      renderOption={contato => {
        const IconTipoCanal = getIconTipoCanal({
          tipoCanal: contato.channelType,
        });

        const isInstagramOrMessengerChannel =
          contato.channelType === TipoCanalEnum.INSTAGRAM ||
          contato.channelType === TipoCanalEnum.MESSENGER;

        const isBlocked = Boolean(contato.blockedAt);

        return (
          <Box style={{ display: 'flex' }}>
            <ListItemAvatar>
              <Badge
                overlap="circular"
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                badgeContent={IconTipoCanal}
              >
                <Avatar src={contato.fotoUrl} />
              </Badge>
            </ListItemAvatar>

            <ListItemText
              style={{ marginLeft: -6 }}
              primary={
                <BlockedContactName name={contato.name} isBlocked={isBlocked} />
              }
              secondary={
                isInstagramOrMessengerChannel
                  ? `Canal: ${contato.channel?.name}`
                  : contato.contact
              }
              primaryTypographyProps={
                { noWrap: true, variant: 'body2', component: 'div' } as Record<
                  string,
                  unknown
                >
              }
              secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
            />
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Contato"
          variant="outlined"
          placeholder="Buscar contato..."
          InputLabelProps={{
            className: classes.truncateValue,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: contatoSelecionado ? (
              <>
                <ContactAvatar
                  src={contatoSelecionado.fotoUrl}
                  className={classes.avatar}
                />
                {params.InputProps.startAdornment}
                <Box mr={0.5} />
              </>
            ) : (
              params.InputProps.startAdornment
            ),
            endAdornment: params.InputProps.endAdornment,
          }}
        />
      )}
    />
  );
}

interface ListboxComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  ListboxComponentProps
>(function ListboxComponent(props, ref) {
  const { children, onLoadMore, hasMore, loading, ...other } = props;

  const itemData = React.Children.toArray(children);
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: itemData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? element => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= itemData.length - 1 && hasMore && !loading) {
      onLoadMore();
    }
  }, [hasMore, loading, itemData.length, virtualItems, onLoadMore]);

  const getHeight = () => {
    const totalSize = virtualizer.getTotalSize();
    const maxHeight = 8 * 50;
    return Math.min(totalSize, maxHeight);
  };

  return (
    <div ref={ref}>
      <div
        ref={parentRef}
        {...other}
        style={{
          height: `${getHeight() + 2 * LISTBOX_PADDING}px`,
          width: '100%',
          overflow: 'auto',
        }}
      >
        <ul
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
            padding: `${LISTBOX_PADDING}px 0`,
            margin: 0,
          }}
        >
          {virtualItems.map(virtualItem => (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${
                  virtualItem.start + LISTBOX_PADDING
                }px)`,
              }}
            >
              {itemData[virtualItem.index]}
            </div>
          ))}
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${
                  virtualizer.getTotalSize() + LISTBOX_PADDING
                }px)`,
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">Carregando...</Typography>
            </div>
          )}
        </ul>
      </div>
    </div>
  );
});
